import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../config/api";

// SECTION: THREAD THUNKS

// NOTE: creating a thread between two different users
export const createThread = createAsyncThunk(
  "thread/create",
  async (userID) => {
    const response = await api.post(`thread/${userID}`);
    return response.data;
  }
);

// NOTE: fetching all threads for current user with pagination, filtering, and sorting
export const fetchThreads = createAsyncThunk(`thread/fetch`, async (params) => {
  const queryParams = new URLSearchParams();

  if (params?.search) queryParams.append("search", params.search);
  if (params?.filter) queryParams.append("filter", params.filter);
  if (params?.page) queryParams.append("page", params.page);
  if (params?.limit) queryParams.append("limit", params.limit);

  const response = await api.get(`threads?${queryParams.toString()}`);
  return response.data;
});

// NOTE: fetching single threads of an user
export const fetchSingleThread = createAsyncThunk(
  `thread/fetch/single`,
  async (id) => {
    const response = await api.get(`thread/${id}`);
    return response.data;
  }
);

// NOTE: fetching messages from a specific thread with pagination
export const fetchThreadMessages = createAsyncThunk(
  `thread/messages/fetch`,
  async (data) => {
    const queryParams = new URLSearchParams();

    if (data?.page) queryParams.append("page", data.page);
    if (data?.limit) queryParams.append("limit", data.limit);

    const response = await api.get(
      `threads/${data.threadId}/messages?${queryParams.toString()}`
    );
    return response.data;
  }
);

// NOTE: fetch unread Thread count
export const fetchUnreadThreadCount = createAsyncThunk(
  "thread/unread",
  async () => {
    const response = await api.get("thread/unread");
    return response.data;
  }
);

// NOTE: Add multiple participants to a thread
export const addParticipantToThread = createAsyncThunk(
  "thread/addParticipant",
  async ({ threadId, participantIds }, { rejectWithValue }) => {
    try {
      const response = await api.post(`threads/${threadId}/participants`, {
        participantIds,
      });
      return response.data;
    } catch (err) {
      console.error("Error adding participants to thread:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to add participants to thread"
      );
    }
  }
);

// NOTE: Remove a participant from a thread
export const removeParticipantFromThread = createAsyncThunk(
  "thread/removeParticipant",
  async ({ threadId, participantId }, { rejectWithValue }) => {
    try {
      const response = await api.delete(
        `threads/${threadId}/participants/${participantId}`
      );
      return response.data;
    } catch (err) {
      console.error("Error removing participant from thread:", err);
      return rejectWithValue(
        err.response?.data?.message ||
          "Failed to remove participant from thread"
      );
    }
  }
);

// NOTE: Leave a thread
export const leaveThread = createAsyncThunk(
  "thread/leave",
  async (threadId, { rejectWithValue }) => {
    try {
      const response = await api.post(`threads/${threadId}/leave`);
      return response.data;
    } catch (err) {
      console.error("Error leaving thread:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to leave thread"
      );
    }
  }
);

// NOTE: Get available referrals (clients) for a thread
export const getAvailableReferralsForThread = createAsyncThunk(
  "thread/getAvailableReferrals",
  async (
    { threadId, page = 1, limit = 5, search = "", selectedClients = [] },
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page", page);
      queryParams.append("limit", limit);
      if (search) queryParams.append("search", search);
      if (selectedClients.length > 0) {
        selectedClients.forEach((id) =>
          queryParams.append("selectedClients", id)
        );
      }

      const response = await api.get(
        `threads/${threadId}/referrals?${queryParams.toString()}`
      );
      return response.data;
    } catch (err) {
      console.error("Error fetching available referrals:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch available referrals"
      );
    }
  }
);

// NOTE: Get available admins (super_admin, sub_admin) for a thread
export const getAvailableAdminsForThread = createAsyncThunk(
  "thread/getAvailableAdmins",
  async (
    { threadId, page = 1, limit = 5, search = "", selectedAdmins = [] },
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page", page);
      queryParams.append("limit", limit);
      if (search) queryParams.append("search", search);
      if (selectedAdmins.length > 0) {
        selectedAdmins.forEach((id) =>
          queryParams.append("selectedAdmins", id)
        );
      }

      const response = await api.get(
        `threads/${threadId}/admins?${queryParams.toString()}`
      );
      return response.data;
    } catch (err) {
      console.error("Error fetching available admins:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch available admins"
      );
    }
  }
);


export const getThreadDetailsByOrder = createAsyncThunk(
  "thread/getDetailsByOrder",
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await api.get(`threads/order/${orderId}`);
      // Return thread data directly
      return {
        data: {
          thread: response.data.data?.thread || response.data.thread,
        },
      };
    } catch (error) {
      console.error("Error fetching thread details by order:", error);
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch thread details" }
      );
    }
  }
);

// NOTE: user accept the offer
// export const acceptOffer = createAsyncThunk("offer/accept", async (data) => {
//     const response = await api.post(`offer/${data?.thread}/${data?.offer}/${data?.user}`);
//     return response.data;
// });

// NOTE: fetch details of selected offer

// NOTE: Update thread name
export const updateThreadName = createAsyncThunk(
  "thread/updateName",
  async ({ threadId, name }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`threads/${threadId}/name`, { name });
      return response.data;
    } catch (err) {
      console.error("Error updating thread name:", err);
      return rejectWithValue(
        err.response?.data?.message || "Failed to update thread name"
      );
    }
  }
);

// NOTE: Get Unread Thread Count
export const getUnreadThreadCount = createAsyncThunk(
  "thread/getUnreadThreadCount",
  async () => {
    const response = await api.get("threads/unread-count");
    return response.data;
  }
);